import type {RefObject} from 'react'
import {useEffect, useRef} from 'react'

interface UseViewportFitHeightOptions {
  /**
   * When false, the height is computed once for the element's final sticky
   * position (ignoring the global header) and only recalculated on window
   * resize.  The panel may extend below the viewport while the header is
   * visible, but stays stable as the user scrolls.
   *
   * @default true
   */
  recalculateOnScroll?: boolean
}

/**
 * Dynamically sets `maxHeight` on the element so it never extends below
 * the viewport bottom.  Recalculates on scroll and resize via
 * requestAnimationFrame to avoid layout thrashing.
 *
 * When `recalculateOnScroll` is false the height is sized for the element's
 * final sticky position (header scrolled away) and only updates on resize,
 * keeping the panel stable while the user scrolls the global header.
 */
export function useViewportFitHeight(ref: RefObject<HTMLElement | null>, options?: UseViewportFitHeightOptions) {
  const rafRef = useRef(0)
  const recalculateOnScroll = options?.recalculateOnScroll ?? true

  useEffect(() => {
    const el = ref.current
    if (!el) return

    // When recalculateOnScroll is false we compute the element's offset
    // within its sticky sidebar so the height matches the final position
    // (header scrolled away) rather than the current viewport position.
    function getOffsetInStickyParent(): number {
      let offset = 0
      let current: HTMLElement | null = el
      while (current) {
        offset += current.offsetTop
        const parent = current.offsetParent as HTMLElement | null
        if (!parent || parent === document.body) break
        const position = getComputedStyle(parent).position
        if (position === 'sticky') {
          // Found the sticky ancestor — offset is now relative to it
          break
        }
        current = parent
      }
      return offset
    }

    function measure() {
      rafRef.current = 0
      if (!el) return
      let available: number
      if (recalculateOnScroll) {
        available = Math.max(0, window.innerHeight - el.getBoundingClientRect().top)
      } else {
        // Size for the final sticky position so the panel is stable
        const offsetInSidebar = getOffsetInStickyParent()
        available = Math.max(0, window.innerHeight - offsetInSidebar)
      }
      el.style.maxHeight = `${available}px`
      // Expose available height so descendant overlay constraints (e.g.
      // embeddedMenuPortalContainer) can size themselves to the panel.
      // Subtract extra space to account for the anchor element and padding
      // so overlays fit above the anchor without Primer flipping direction.
      el.style.setProperty('--panel-height', `${available - 30}px`)
    }

    function scheduleUpdate() {
      if (rafRef.current) return
      rafRef.current = requestAnimationFrame(measure)
    }

    // Initial measurement
    measure()

    // IntersectionObserver cannot be used here: setting maxHeight on the
    // observed element changes its intersection, creating a feedback loop.
    // Passive scroll + rAF is the simplest correct approach for viewport-clamping.
    if (recalculateOnScroll) {
      // eslint-disable-next-line github/prefer-observers
      window.addEventListener('scroll', scheduleUpdate)
    }
    // eslint-disable-next-line github/prefer-observers
    window.addEventListener('resize', scheduleUpdate)

    return () => {
      if (recalculateOnScroll) {
        window.removeEventListener('scroll', scheduleUpdate)
      }
      window.removeEventListener('resize', scheduleUpdate)
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      el.style.maxHeight = ''
      el.style.removeProperty('--panel-height')
    }
  }, [ref, recalculateOnScroll])
}
